import React, { FormEvent, useEffect, useState } from "react";

import { Upload } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Link,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";

import mainStyles from "../../styles";
import { RegionCode } from "../../types/region";
import { searchTableIndex } from "./constants";
import {
  bulkImportContent,
  downloadTemplate,
} from "api/endpoints/content-library";
import DragAndDrop, { FileToReturn } from "components/shared/drag-and-drop";
import { downloadFileFromUrl } from "helpers/download-file";

type ErrorSource = "upload_error" | "sample_template_error" | undefined;
type ImportResult = {
  filename: string;
  region: string;
  logUrl: string;
};

const regionMap = searchTableIndex.reduce(
  (acc, { regionCode, tableHeading }) => {
    acc[regionCode] = tableHeading;
    return acc;
  },
  {} as Record<RegionCode, string>,
);

const ContentUploader = () => {
  const [formState, setFormState] = useState<FileToReturn[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [errorSource, setErrorSource] = useState<ErrorSource>();
  const [sampleUrl, setSampleUrl] = useState("");
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const classes = mainStyles();

  useEffect(() => {
    downloadTemplate()
      .then((res) => {
        setSampleUrl(res.url);
      })
      .catch(() => {
        setErrorSource("sample_template_error");
      });
  }, []);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    try {
      const targetRegionString = form.get("target region");
      if (
        typeof targetRegionString !== "string" ||
        !(targetRegionString in regionMap)
      ) {
        setErrorSource("upload_error");
        return;
      }
      const targetRegionCode = targetRegionString as RegionCode;

      setIsUploading(true);
      const res = await bulkImportContent(formState[0], targetRegionCode);
      const regionLabel = regionMap[targetRegionCode];
      setImportResults((prev) => [
        ...prev,
        {
          filename: formState[0].name,
          region: regionLabel,
          logUrl: res.url,
        },
      ]);
      downloadFileFromUrl(`${formState[0].name}_${regionLabel}`, res.url); // auto download the resultant log file
      setFormState([]);
    } catch {
      setErrorSource("upload_error");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Typography variant="h5" align="left" sx={{ marginTop: "1rem" }}>
        Bulk Import products in Content Library
      </Typography>
      <Paper
        sx={{
          p: 2,
          maxWidth: "600px",
          display: "flex",
          gap: "1rem",
          marginBottom: "15rem",
        }}
      >
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column", width: "100%" }}
          onSubmit={onSubmit}
        >
          <DownloadSampleLink sampleUrl={sampleUrl} />
          <Box
            sx={{
              "&>div:first-child": {
                aspectRatio: "3",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              },
            }}
          >
            <DragAndDrop
              acceptedFileTypes={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [".xlsx"],
              }}
              setDragAndDropFiles={setFormState}
              maxAcceptedFiles={1}
              files={formState}
              setFiles={setFormState}
              removeFilePreview={(index: number) => {
                setFormState((prev) => prev.filter((_, i) => i !== index));
              }}
            />
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              gap: "1rem",
              justifyContent: "space-between",
              paddingTop: "1rem",
            }}
          >
            <FormControl
              variant="outlined"
              size="small"
              sx={classes.formControl}
            >
              <InputLabel id="select-target-region-select-label">
                Target Region
              </InputLabel>
              <Select
                labelId="select-target-region-select-label"
                defaultValue="au"
                id="select-target-region"
                label="Target Region"
                name="target region"
                required
              >
                {searchTableIndex.map((region) => (
                  <MenuItem key={region.regionCode} value={region.regionCode}>
                    {region.tableHeading}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              disabled={formState.length === 0 || isUploading}
              variant="contained"
              startIcon={<Upload />}
              type="submit"
            >
              {isUploading ? "Uploading" : "Upload spreadsheet"}
            </Button>
          </Box>
          <UploadResultSection imports={importResults} />
        </Box>
      </Paper>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={!!errorSource}
        autoHideDuration={7000}
        onClose={() => setErrorSource(undefined)}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorSource(undefined)}
        >
          {errorSource === "upload_error"
            ? "Failed to import products to content library, Please try again"
            : "Failed to download sample template, Please try again"}
        </Alert>
      </Snackbar>
    </>
  );
};

function DownloadSampleLink({ sampleUrl }: { sampleUrl?: string }) {
  return sampleUrl ? (
    <Link
      sx={{ textDecoration: "none", alignSelf: "flex-end" }}
      download
      href={sampleUrl}
    >
      Download Sample
    </Link>
  ) : (
    <Typography
      color="textSecondary"
      sx={{ cursor: "not-allowed", alignSelf: "flex-end" }}
    >
      Download sample
    </Typography>
  );
}

interface UploadResultSectionProps {
  imports: ImportResult[];
}

function UploadResultSection({ imports }: UploadResultSectionProps) {
  if (imports.length < 1) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        marginTop: "2rem",
      }}
    >
      <Typography id="upload-results" variant="body1">
        Uploaded files
      </Typography>
      <List aria-labelledby="upload-results">
        {imports.map((submission, idx) => (
          <ListItem key={idx}>
            <Chip label={submission.filename} /> uploaded to{" "}
            <Chip label={submission.region} />(
            <Link
              href={submission.logUrl}
              download
              sx={{ textDecoration: "none" }}
            >
              Download log
            </Link>
            )
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

function Chip({ label }: { label: string }) {
  return (
    <Box
      component="span"
      sx={{
        padding: "0.25rem 0.5rem",
        backgroundColor: "#eee",
        border: "1px solid #ccc",
        margin: "0 0.25rem",
      }}
    >
      {label}
    </Box>
  );
}

export default ContentUploader;
